import type {ReactElement, ReactNode} from "react";
import type React from "react";
import {cloneElement, useState} from "react";
import {StyleSheet} from "react-native";
import {KeyboardAwareScrollView, KeyboardStickyView} from "react-native-keyboard-controller";

import {useView} from "../../hooks/useView";
import type {IButtonProps} from "../ui/button";
import {VStack} from "../ui/vstack";

import ModalBody from "./ModalBody";
import ModalFooter from "./ModalFooter";
import ModalHeader from "./ModalHeader";

type Props = {
    title?: string;
    subTitle?: ReactNode;
    children: ReactNode;
    isStickyFooter?: boolean;
    useDefaultDivider?: boolean;
    isLoading?: boolean;
    onHide?: () => void;
} & StrictUnion<
    | {
          primaryButton: ReactElement<IButtonProps>;
          useGhostVariantOnMobileWeb?: boolean;
          footer?: ReactNode;
      }
    | {
          rightTitleElement?: ReactNode;
          footer?: ReactNode;
      }
>;

const styles = StyleSheet.create({
    container: {
        flex: 1
    }
});

// this is used for mobile view as well as on app
const ModalLayout: React.FC<Props> = ({
    title,
    subTitle,
    children,
    rightTitleElement,
    isStickyFooter = true,
    footer,
    primaryButton,
    useGhostVariantOnMobileWeb = true,
    useDefaultDivider = true,
    isLoading,
    onHide
}) => {
    const view = useView();
    const [footerHeight, setFooterHeight] = useState(0);

    // this logic is based on the design purpose
    // which is too complicated
    const rightElement =
        view === "webMobile"
            ? primaryButton
                ? useGhostVariantOnMobileWeb
                    ? cloneElement<IButtonProps>(primaryButton, {
                          no110MinWidth: true,
                          variant: "ghost"
                      })
                    : primaryButton
                : rightTitleElement
            : rightTitleElement;

    const actualFooter = view !== "webMobile" ? primaryButton ?? footer : footer;

    return (
        <VStack className="flex-1 bg-white" testID="drawer-layout">
            {(title || subTitle) && (
                <ModalHeader
                    rightElement={rightElement}
                    subTitle={subTitle}
                    title={title}
                    onPress={onHide}
                />
            )}
            {process.env.IS_WEB ? (
                <>
                    <ModalBody isLoading={isLoading} useDefaultDivider={useDefaultDivider}>
                        {children}
                    </ModalBody>
                    {actualFooter &&
                        (isStickyFooter ? (
                            <ModalFooter isSticky onLayoutChange={setFooterHeight}>
                                {actualFooter}
                            </ModalFooter>
                        ) : (
                            <ModalFooter>{actualFooter}</ModalFooter>
                        ))}
                </>
            ) : (
                <>
                    <KeyboardAwareScrollView
                        bottomOffset={footerHeight + 50}
                        // keep the keyboard open when tapping outside of the input
                        keyboardShouldPersistTaps="handled"
                        style={styles.container}
                        testID="modal-body-scroll-view"
                        {...(isLoading ? {contentContainerStyle: {flex: 1}} : {})}
                    >
                        <ModalBody isLoading={isLoading} useDefaultDivider={useDefaultDivider}>
                            {children}
                        </ModalBody>
                    </KeyboardAwareScrollView>
                    {actualFooter &&
                        (isStickyFooter ? (
                            <KeyboardStickyView>
                                <ModalFooter isSticky onLayoutChange={setFooterHeight}>
                                    {actualFooter}
                                </ModalFooter>
                            </KeyboardStickyView>
                        ) : (
                            <ModalFooter>{actualFooter}</ModalFooter>
                        ))}
                </>
            )}
        </VStack>
    );
};

export default ModalLayout;
